import React, { useState } from "react";
import * as Yup from "yup";
import { toast } from "react-toastify";
import FormDefault from "../../components/Forms/FormDefault";
import { requestAuth } from "../../components/services/RequestService";
import dayjs from "dayjs";

const NewProcedure = (props) => {
  const { reload, onClose, isQuotation = false, dims = false } = props;

  const [dimStatuses, setDimStatuses] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [lastValues, setLastValues] = useState(null);

  // Process a single DIM file end-to-end: create procedure, upload file, create dataset, read DIM.
  // If any step fails after retries, attempt to rollback (delete procedure) to avoid leaving incomplete records.
  const processSingleDim = async (file, values, fieldId, index, total, onStatus) => {
    const maxAttempts = 3;
    let procedureId = null;

    try {
      toast.info(`Creando trámite para ${file.name} (${index}/${total})`);

      const procedureData = {
        ...values,
        numberItems: 1,
        Category: "normal",
        Step: 5,
      };

      const procResp = await requestAuth(
        "post",
        isQuotation ? `/procedure/simple` : `/procedure`,
        procedureData,
      );

      procedureId = procResp.data.id;
      toast.info(`Trámite ${procedureId} creado para ${file.name}`);
      if (onStatus) onStatus("created", { procedureId });

      // Helper to retry async actions
      const withRetries = async (fn, desc) => {
        let lastError = null;
        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
          try {
            return await fn();
          } catch (err) {
            lastError = err;
            console.warn(`${desc} intento ${attempt} falló:`, err);
            if (attempt < maxAttempts) {
              await new Promise((r) => setTimeout(r, 1000 * attempt));
            }
          }
        }
        throw lastError;
      };

      toast.info(`Subiendo ${file.name} (${index}/${total})`);

      const fileResponse = await withRetries(async () => {
        const formData = new FormData();
        formData.append("Name", "DIM");
        formData.append("File", file);
        formData.append("originalName", file.name);
        formData.append("Status", "verified");
        formData.append("ProcedureId", procedureId);

        return await requestAuth("post", "/files", formData);
      }, `Subida de archivo ${file.name}`);

      if (onStatus) onStatus("uploaded", { fileId: fileResponse.data.id, url: fileResponse.data.url });
      toast.success(`Archivo ${file.name} subido`);

      const datasetData = [
        {
          Value: fileResponse.data.url,
          ProcedureId: procedureId,
          FieldId: fieldId,
          FileId: fileResponse.data.id,
          name: "adjuntarDim",
          url: "adjuntarDim",
          type: "fileRead",
        },
      ];

      await withRetries(async () => {
        return await requestAuth("post", "/dataset", datasetData);
      }, `Creación de dataset para ${file.name}`);

      if (onStatus) onStatus("dataset", {});

      toast.success(`Dataset creado para ${file.name}`);

      toast.info(`Leyendo DIM ${file.name} en trámite ${procedureId}`);
      if (onStatus) onStatus("reading", {});
      await withRetries(async () => {
        return await readDimProcess(procedureId, fieldId);
      }, `Lectura DIM para ${file.name}`);

      toast.success(`DIM ${file.name} procesado correctamente`);
      if (onStatus) onStatus("finished", {});

      return { fileName: file.name, procedureId, success: true };
    } catch (error) {
      console.error(`Error procesando ${file.name}:`, error);
      toast.error(`Error procesando ${file.name}: ${error.message || error}`);
      if (onStatus) onStatus("failed", { error });

      // Rollback: try to delete the procedure so we don't leave incomplete records
      if (procedureId) {
        try {
          await requestAuth("delete", `/procedure/${procedureId}`);
          toast.info(`Trámite ${procedureId} eliminado por error en ${file.name}`);
        } catch (delErr) {
          console.error(`No se pudo eliminar trámite ${procedureId}:`, delErr);
          toast.warn(`No se pudo eliminar trámite ${procedureId}. Revisar manualmente.`);
        }
      }

      return { fileName: file.name, procedureId, success: false, error };
    } finally {
      // Small delay to avoid hammering the API
      await new Promise((resolve) => setTimeout(resolve, 500));
    }
  };

  const readDimProcess = async (procedureId, fieldId) => {
    try {
      toast.info(`Leyendo DIM para trámite ${procedureId}...`);

      const result = await requestAuth(
        // "put",
        // `/files/convertPdfToCsv/${procedureId}?FieldId=${fieldId}`,
        "post",
        `/v2/procedureExtractions/${procedureId}/dim`,
        null,
        {
          getOnly: false,
          includeItems: false
        },
      );
      toast.success(`Datos DIM obtenidos para trámite ${procedureId}`);
      return result;
    } catch (error) {
      console.error(`Error leyendo DIM para trámite ${procedureId}:`, error);
      toast.error(`Error leyendo DIM para trámite ${procedureId}`);
      throw error;
    }
  };

  const fieldDIM = dims
    ? [
      {
        label: "DIMs",
        name: "dims",
        typeInput: "files",
        multiple: true,
      },
    ]
    : [];

  return (
    <div>
      <FormDefault
        tittle="Agregar nuevo trámite"
        fields={[
          {
            label: "Compañia",
            name: "CompanyId",
            typeInput: "select",
            urlApi: "/company/getCompaniesBySelect",
            value: "id",
            labelOption: "razonSocial",
          },
          {
            label: "Tipo de procedimiento",
            name: "ProcedureTypeId",
            typeInput: "select",
            urlApi: "/procedureType",
            value: "id",
            labelOption: "name",
          },
          ...fieldDIM,
        ]}
        initialValues={{
          CompanyId: "",
          ProcedureTypeId: 1,
          city: "LP",
          year: dayjs().format("YYYY"),
          IsQuotation: isQuotation,
          Category: "normal",
          numberItems: 1,
        }}
        validationSchema={Yup.object().shape({
          CompanyId: Yup.string().required("Este campo es obligatorio"),
          ProcedureTypeId: Yup.string().required("Este campo es obligatorio"),
          ...(dims
            ? {
              dims: Yup.mixed().test(
                "required",
                "Se requiere al menos un archivo DIM",
                (value) => {
                  if (!dims) return true;
                  return value && value.length > 0;
                },
              ),
            }
            : {}),
        })}
        onSubmit={async (values) => {
          try {
            if (dims && values.dims && values.dims.length > 0) {
              const files = values.dims;
              const fieldId = 40;
              toast.info(`Procesando ${files.length} DIMs secuencialmente...`);

              setProcessing(true);
              setLastValues(values);
              // initialize statuses (support FileList by converting to Array)
              setDimStatuses(
                Array.from(files).map((f) => ({ fileName: f.name, file: f, status: "pending", procedureId: null, error: null })),
              );

              const results = [];

              const filesArr = Array.from(files);
              for (let i = 0; i < filesArr.length; i++) {
                const file = filesArr[i];
                setDimStatuses((prev) => prev.map((p) => (p.fileName === file.name ? { ...p, status: "processing" } : p)));

                const onStatus = (step, info) => {
                  setDimStatuses((prev) =>
                    prev.map((p) => {
                      if (p.fileName !== file.name) return p;
                      return {
                        ...p,
                        step,
                        procedureId: info && info.procedureId ? info.procedureId : p.procedureId,
                        fileId: info && info.fileId ? info.fileId : p.fileId,
                        url: info && info.url ? info.url : p.url,
                        error: info && info.error ? info.error : p.error,
                        status: step === "failed" ? "failed" : step === "finished" ? "done" : p.status,
                      };
                    }),
                  );
                };

                const res = await processSingleDim(file, values, fieldId, i + 1, filesArr.length, onStatus);

                // ensure final status set if not already
                setDimStatuses((prev) =>
                  prev.map((p) =>
                    p.fileName === file.name
                      ? { ...p, status: res.success ? "done" : "failed", procedureId: res.procedureId || null, error: res.error || null }
                      : p,
                  ),
                );

                results.push(res);
                // small pause between files
                await new Promise((r) => setTimeout(r, 300));
              }

              setProcessing(false);

              const successful = results.filter((r) => r.success).length;
              const failed = results.filter((r) => !r.success);

              if (successful === files.length) {
                toast.success(`Se procesaron ${successful} de ${files.length} DIMs correctamente`);
                reload();
                onClose();
                // clear statuses
                setDimStatuses([]);
                setLastValues(null);
              } else {
                toast.error(`Se procesaron ${successful} de ${files.length} DIMs. ${failed.length} fallaron.`);
                console.error("Detalles de DIMs fallidos:", failed);
                // Leave the modal open so user can inspect/correct and reintentar
              }
            } else {
              await requestAuth(
                "post",
                isQuotation ? `/procedure/simple` : `/procedure`,
                values,
              );
              reload();
              onClose();
              toast.success("Trámite agregado correctamente!!!");
            }
          } catch (error) {
            console.error("Error en el proceso:", error);
            toast.error("Se ha producido un error al crear el Proceso");
          }
        }}
        buttonName={dims ? "Procesar DIMs secuencialmente" : "Enviar"}
      />

      {dimStatuses && dimStatuses.length > 0 && (
        <div className="mt-4 p-3 border rounded bg-white shadow-sm">
          <h4 className="text-lg font-medium mb-2">Estado por archivo</h4>
          <ul className="space-y-2">
            {dimStatuses.map((s) => (
              <li key={s.fileName} className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded bg-gray-100">
                    {s.step === "created" && (
                      <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                      </svg>
                    )}
                    {s.step === "uploaded" && (
                      <svg className="w-5 h-5 text-indigo-500" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M12 3v12m0 0l4-4m-4 4l-4-4" />
                      </svg>
                    )}
                    {s.step === "dataset" && (
                      <svg className="w-5 h-5 text-yellow-500" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M3 6h18M3 12h18M3 18h18" />
                      </svg>
                    )}
                    {s.step === "reading" && (
                      <svg className="w-5 h-5 text-purple-500 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <circle cx="12" cy="12" r="10" strokeWidth="4" strokeOpacity="0.2" />
                        <path d="M22 12a10 10 0 0 0-10-10" strokeWidth="4" strokeLinecap="round" />
                      </svg>
                    )}
                    {s.step === "finished" && (
                      <svg className="w-5 h-5 text-green-500" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                    {!s.step && s.status === "pending" && <span className="text-sm text-gray-500">—</span>}
                  </div>

                  <div>
                    <div className="font-medium">{s.fileName}</div>
                    <div className="text-xs text-gray-500">
                      {s.step === "created" && "Trámite creado"}
                      {s.step === "uploaded" && "Documento cargado"}
                      {s.step === "dataset" && "Dataset creado"}
                      {s.step === "reading" && "Leyendo DIM"}
                      {s.step === "finished" && <span className="text-green-600">Finalizado</span>}
                      {!s.step && s.status === "pending" && "Pendiente"}
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  {s.status === "failed" && (
                    <div className="text-red-600 text-sm">Falló</div>
                  )}
                  {s.status === "done" && (
                    <div className="text-green-600 text-sm">OK</div>
                  )}
                </div>
              </li>
            ))}
          </ul>

          <div className="mt-4 flex items-center space-x-2">
            <button
              type="button"
              className={`px-3 py-1 rounded text-sm font-medium ${processing || !dimStatuses.some((d) => d.status === "failed") ? "bg-gray-300 text-gray-600" : "bg-blue-600 text-white hover:bg-blue-700"}`}
              disabled={processing || !dimStatuses.some((d) => d.status === "failed")}
              onClick={async () => {
                if (!lastValues) return;
                setProcessing(true);
                const failedItems = dimStatuses.filter((d) => d.status === "failed");
                const failedArr = Array.from(failedItems);
                for (let i = 0; i < failedArr.length; i++) {
                  const item = failedArr[i];
                  setDimStatuses((prev) => prev.map((p) => (p.fileName === item.fileName ? { ...p, status: "processing", error: null } : p)));

                  const onStatus = (step, info) => {
                    setDimStatuses((prev) =>
                      prev.map((p) => {
                        if (p.fileName !== item.fileName) return p;
                        return {
                          ...p,
                          step,
                          procedureId: info && info.procedureId ? info.procedureId : p.procedureId,
                          fileId: info && info.fileId ? info.fileId : p.fileId,
                          url: info && info.url ? info.url : p.url,
                          error: info && info.error ? info.error : p.error,
                          status: step === "failed" ? "failed" : step === "finished" ? "done" : p.status,
                        };
                      }),
                    );
                  };

                  const res = await processSingleDim(item.file, lastValues, 40, i + 1, failedArr.length, onStatus);
                  setDimStatuses((prev) => prev.map((p) => (p.fileName === item.fileName ? { ...p, status: res.success ? "done" : "failed", procedureId: res.procedureId || null, error: res.error || null } : p)));
                }
                setProcessing(false);

                // if all done after retry, close
                if (dimStatuses.every((d) => d.status === "done" || d.status === "pending")) {
                  const allDone = dimStatuses.every((d) => d.status === "done");
                  if (allDone) {
                    reload();
                    onClose();
                    setDimStatuses([]);
                    setLastValues(null);
                  }
                }
              }}
            >
              Reintentar fallidos
            </button>

            <button
              type="button"
              className="px-3 py-1 rounded text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200"
              onClick={() => {
                // clear statuses if user wants to dismiss
                setDimStatuses([]);
              }}
            >
              Cerrar estado
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewProcedure;
